import { toaster } from "@/components/ui/toaster"
import { Box, Button, Flex, Image, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"
import type { ChangeEvent } from "react"

import { OrganizationsService } from "@/client"
import type { OrganizationPublic } from "@/client"

interface BrandingLogoUploadProps {
  organization: OrganizationPublic
}

export function BrandingLogoUpload({ organization }: BrandingLogoUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      return OrganizationsService.uploadOrganizationLogo({
        orgId: organization.id,
        formData: { file },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding", organization.id] })
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      queryClient.invalidateQueries({
        queryKey: ["organization", organization.id],
      })
      toaster.create({
        title: "Logo uploaded successfully",
        type: "success",
        duration: 3000,
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Upload failed",
        description: error.message || "Failed to upload logo",
        type: "error",
        duration: 5000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () =>
      OrganizationsService.deleteOrganizationLogo({ orgId: organization.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding", organization.id] })
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      queryClient.invalidateQueries({
        queryKey: ["organization", organization.id],
      })
      toaster.create({
        title: "Logo deleted successfully",
        type: "success",
        duration: 3000,
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Delete failed",
        description: error.message || "Failed to delete logo",
        type: "error",
        duration: 5000,
      })
    },
  })

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      uploadFile(file)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(false)

    const file = event.dataTransfer.files[0]
    if (file) {
      uploadFile(file)
    }
  }

  const uploadFile = (file: File) => {
    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg|webp)$/)) {
      toaster.create({
        title: "Invalid file type",
        description: "Please upload a PNG, JPEG, or WEBP image",
        type: "error",
        duration: 5000,
      })
      return
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toaster.create({
        title: "File too large",
        description: "Maximum file size is 10MB",
        type: "error",
        duration: 5000,
      })
      return
    }

    uploadMutation.mutate(file)
  }

  const currentLogoUrl = organization.logo_url || "/assets/images/qc-logo2.png"

  return (
    <Box>
      <Flex gap={8} align="flex-start">
        {/* Logo Preview */}
        <VStack>
          <Box
            w="150px"
            h="150px"
            border="2px dashed"
            borderColor="gray.300"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.50"
            p={4}
          >
            <Image
              src={currentLogoUrl}
              alt="Current logo"
              objectFit="contain"
            />
          </Box>
          {organization.logo_url && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
            >
              Delete Logo
            </Button>
          )}
        </VStack>

        {/* Upload Controls */}
        <Box flex={1}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          <Box
            border="2px dashed"
            borderColor={isDragging ? "blue.500" : "blue.300"}
            borderRadius="md"
            p={8}
            textAlign="center"
            bg={isDragging ? "blue.50" : "blue.25"}
            cursor="pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            transition="all 0.2s"
          >
            <Text fontSize="3xl" mb={2}>
              📤
            </Text>
            <Text fontWeight="medium" color="blue.700" mb={1}>
              Click to upload or drag and drop
            </Text>
            <Text fontSize="sm" color="gray.600">
              PNG, JPG, WEBP (max 10MB)
            </Text>
            {uploadMutation.isPending && (
              <Text fontSize="sm" color="blue.600" mt={2}>
                Uploading...
              </Text>
            )}
          </Box>

          <Box
            mt={4}
            bg="yellow.50"
            p={3}
            borderRadius="md"
            borderLeft="3px solid"
            borderColor="yellow.500"
          >
            <Text fontSize="sm" color="yellow.900">
              <strong>Note:</strong> Images will be automatically resized to
              200×200px and compressed for optimal loading speed.
            </Text>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
